import React, { useState, FormEvent } from 'react';

interface Props {
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (email: string, password: string, name: string) => Promise<void>;
}

export function AuthPage({ onLogin, onRegister }: Props) {
  const [mode, setMode]         = useState<'login' | 'register'>('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [error, setError]       = useState('');
  const [busy, setBusy]         = useState(false);

  const extractError = (err: unknown): string => {
    const e = err as { response?: { data?: { detail?: string } } };
    return e?.response?.data?.detail ?? 'Something went wrong. Try again.';
  };

  const handleSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'login') {
        await onLogin(email, password);
      } else {
        await onRegister(email, password, name);
      }
    } catch (err) {
      setError(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logo}>☀</div>
          <h1 style={styles.title}>Solar Farm Layout</h1>
          <p style={styles.sub}>Utility-scale feasibility engine</p>
        </div>

        {/* Tab switcher */}
        <div style={styles.tabs}>
          {(['login', 'register'] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(''); }}
              style={{ ...styles.tab, ...(mode === m ? styles.tabActive : {}) }}
            >
              {m === 'login' ? 'Log in' : 'Create account'}
            </button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === 'register' && (
            <div style={styles.field}>
              <label style={styles.label}>Full name</label>
              <input
                style={styles.input}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Wilson"
                autoComplete="name"
              />
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'register' ? 'Min 8 chars, one letter + one number' : ''}
              required
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" style={styles.submit} disabled={busy}>
            {busy ? '…' : mode === 'login' ? 'Log in' : 'Create account'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1F3864 0%, #17607A 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    width: 420,
    maxWidth: '90vw',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #1F3864, #2E5FA3)',
    padding: '28px 32px',
    textAlign: 'center',
  },
  logo: {
    fontSize: 36,
    marginBottom: 8,
  },
  title: {
    margin: '0 0 4px',
    color: '#fff',
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '-0.3px',
  },
  sub: {
    margin: 0,
    color: 'rgba(255,255,255,0.65)',
    fontSize: 13,
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #e8ecf0',
  },
  tab: {
    flex: 1,
    padding: '14px 0',
    border: 'none',
    background: '#f7f9fc',
    color: '#666',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  tabActive: {
    background: '#fff',
    color: '#1F3864',
    fontWeight: 700,
    borderBottom: '2px solid #2E5FA3',
  },
  form: {
    padding: '28px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#404040',
  },
  input: {
    border: '1.5px solid #d0d7e3',
    borderRadius: 7,
    padding: '10px 12px',
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.15s',
    fontFamily: 'inherit',
  },
  error: {
    background: '#fde8e8',
    border: '1px solid #f8b4b4',
    borderRadius: 7,
    padding: '10px 12px',
    fontSize: 13,
    color: '#8b1a1a',
  },
  submit: {
    background: 'linear-gradient(135deg, #1F3864, #2E5FA3)',
    color: '#fff',
    border: 'none',
    borderRadius: 7,
    padding: '12px 0',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
    transition: 'opacity 0.15s',
  },
};
